// §8.4 Tier-1 visual parity — LoginPage matches frontend/src/app/login/login-client.tsx
// byte-for-byte on the structural JSX. Particles, card decoration, logo header all ported.
// Next.js deps replaced: `next/image` → <img>, useRouter → useNavigate (via context).
//
// Android S8 / Chrome WebView ~90 (#android-s8-login-flow) + issue #176:
//   • `min-h-dvh` was added in Chromium 108 — older WebViews ignore it and
//     the wrapper collapses, sending the form below the fold. We anchor the
//     wrapper with `min-h-screen` (100vh, universally supported) *before*
//     the `min-h-dvh` override so modern engines still get the dynamic
//     viewport while legacy WebViews keep a usable height.
//   • The Chrome WebView IME shrinks the layout viewport when an input
//     receives focus on mount. We force `window.scrollTo(0, 0)` on mount so
//     the LogoHeader stays in view instead of the form bottom flashing in.
//   • `EmailStep`/`LoginStep` no longer use `autoFocus` — the IME used to
//     pop up immediately and slide the LoginCard below the address bar
//     (operator report 2026-05-28: "fica fora de foco o login total").
//   • Card width is computed from measured visualViewport pixels instead of
//     CSS `min()`/`100vw`, which avoids WebView 90 stale viewport math
//     after URL-bar or keyboard transitions (issue #176 Surface #1).
//   • Centring: `items-center justify-center` without the `sm:` breakpoint
//     so 360px viewports stop anchoring the card to the top. The legacy
//     WebView IME case is handled by `overflow-y-auto` on the wrapper +
//     `pb-[env(keyboard-inset-height,0)]` for Chromium ≥108
//     (issue #176 Surface #2).
import {
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
  type CSSProperties,
  type ReactNode,
} from "react";
import { useQuery } from "@/lib/query";
import { useLocation, useNavigate } from "react-router-dom";
import { LoginFormProvider } from "../components/LoginFormContext";
import { useLoginFormContext, useSession } from "../hooks";
import {
  EmailStep,
  LoginStep,
  PaymentStep,
  MatrixStep,
  ParticlesBackground,
} from "../components/LoginForm";
import { TwoFactorStep } from "../components/TwoFactorStep";
import { fetchPublicPlans } from "../api";
import type { LoginFlowReturn, PublicPlanView, SessionView } from "../types";
import { useSupportWhatsApp } from "@/features/support";
import {
  addVisualViewportListeners,
  readVisualViewportRect,
  useVisualViewportRect,
} from "@/lib/viewport/visualViewport";
import { resolveLoginCardMaxWidth } from "./loginViewport";

const LOGO_SRC = "/brand/quartelbot-logo-white.webp";
const LOGO_ALT = "Quartel Bot Logo";
const LOGIN_FAB_LINK_CLASS_NAME =
  "group pointer-events-auto relative flex h-14 w-14 items-center justify-center rounded-full bg-success text-success-foreground shadow-lg ring-1 ring-border transition-transform hover:scale-105 active:scale-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";
const LOGIN_FAB_TOOLTIP_CLASS_NAME =
  "pointer-events-none absolute right-full mr-3 top-1/2 -translate-y-1/2 hidden whitespace-nowrap rounded-lg bg-background/80 px-2.5 py-1.5 text-[11px] text-foreground/80 opacity-0 shadow-xl ring-1 ring-border transition-opacity duration-150 group-hover:opacity-100 [@media(hover:hover)]:block";

// ── Background decoration ──────────────────────────────────────────────────────

function BackgroundAmbience(): React.JSX.Element {
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      <div className="absolute inset-x-0 top-0 h-[45%] bg-[image:var(--login-ambience-top)]" />
      <div className="absolute inset-x-0 bottom-0 h-[45%] bg-[image:var(--login-ambience-bottom)]" />
    </div>
  );
}

function CardDecoration(): React.JSX.Element {
  return (
    <div
      className="absolute inset-0 pointer-events-none"
      aria-hidden="true"
      data-testid="login-card-decor"
    >
      <div className="absolute inset-0 rounded-3xl bg-[image:var(--login-card-decor-top)] opacity-40" />
      <div className="absolute inset-0 rounded-3xl bg-[image:var(--login-card-decor-cyan)]" />
      <div className="absolute inset-0 rounded-3xl bg-[image:var(--login-card-decor-blue)]" />
      <div className="absolute inset-0 rounded-3xl bg-[image:var(--login-card-decor-border)] p-[1px]" />
    </div>
  );
}

function LogoHeader({ subtitle }: { subtitle: string }): React.JSX.Element {
  return (
    <div className="relative z-10 flex flex-col items-center mb-9">
      <div className="relative w-52 h-16 mb-4 flex items-center justify-center md:group-hover/card:animate-float transition-all">
        <LoginLogoImage />
      </div>
      <div className="flex items-center gap-3 text-xs uppercase tracking-[0.35em] text-foreground/50 font-semibold text-center">
        <span className="h-px w-10 bg-gradient-to-r from-transparent via-brand-cyan/50 to-transparent" />
        <span>{subtitle}</span>
        <span className="h-px w-10 bg-gradient-to-r from-transparent via-brand-cyan/50 to-transparent" />
      </div>
    </div>
  );
}

function LoginLogoImage(): React.JSX.Element {
  return (
    <img
      src={LOGO_SRC}
      alt={LOGO_ALT}
      fetchPriority="high"
      width={520}
      height={214}
      className="h-full w-full object-contain [filter:var(--login-logo-filter)] drop-shadow-sm"
    />
  );
}

// ── Step content router ────────────────────────────────────────────────────────

function StepContent({
  flow,
  plans,
}: {
  flow: LoginFlowReturn;
  plans: readonly PublicPlanView[];
}): React.JSX.Element | null {
  if (flow.step === "email")
    return <EmailStep flow={flow} checkoutUrl={flow.checkoutUrl} />;
  if (flow.step === "need_payment")
    return <PaymentStep flow={flow} plans={plans} />;
  if (flow.step === "need_matrix") return <MatrixStep flow={flow} />;
  if (flow.step === "need_2fa") return <TwoFactorStep flow={flow} />;
  if (flow.step === "login") return <LoginStep flow={flow} />;
  return null;
}

// ── Inner card (needs context) ─────────────────────────────────────────────────

function LoginCard({
  plans,
}: {
  plans: readonly PublicPlanView[];
}): React.JSX.Element {
  const flow = useLoginFormContext();
  const location = useLocation();
  const notice = loginNotice(new URLSearchParams(location.search));
  const cardMaxWidth = useLoginCardMaxWidth();
  return (
    <LoginViewportShell>
      <ParticlesBackground />
      <div className="pointer-events-none absolute inset-0 z-0 bg-[image:var(--login-page-radial)]" />
      <BackgroundAmbience />
      <div
        data-testid="login-form-card"
        className="relative z-10 mx-auto w-full max-w-[430px] group/card"
        style={{ maxWidth: `${cardMaxWidth}px` }}
      >
        <div className="relative overflow-hidden rounded-3xl border border-[color:var(--login-card-border)] bg-[image:var(--login-card-bg)] p-8 shadow-[var(--login-card-shadow)] backdrop-blur-2xl md:hover:scale-[1.01]">
          <CardDecoration />
          <LogoHeader subtitle={flow.getSubtitle()} />
          {notice && <LoginNotice message={notice} />}
          <StepContent flow={flow} plans={plans} />
        </div>
      </div>
      <LoginWhatsAppFab />
    </LoginViewportShell>
  );
}

function LoginViewportShell(props: {
  readonly children: ReactNode;
}): React.JSX.Element {
  const style = useLoginViewportShellStyle();
  useLayoutEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  return (
    <div
      data-testid="login-viewport-shell"
      className="min-h-screen select-none bg-[var(--login-page-bg)] text-[var(--login-page-fg)] flex items-center justify-center p-4 pt-[max(env(safe-area-inset-top),1rem)] pb-[max(env(keyboard-inset-height,0px),1rem)] relative overflow-x-hidden overflow-y-auto lg:perspective-1000"
      style={style}
    >
      {props.children}
    </div>
  );
}

function useLoginViewportShellStyle(): CSSProperties {
  const rect = useVisualViewportRect();
  return useMemo<CSSProperties>(
    () => ({
      minHeight: `${Math.round(rect.height)}px`,
      paddingTop: `calc(${Math.round(rect.top)}px + max(env(safe-area-inset-top), 1rem))`,
    }),
    [rect.height, rect.top],
  );
}

function readLoginCardMaxWidth(): number {
  if (typeof window === "undefined") return resolveLoginCardMaxWidth(430);
  return resolveLoginCardMaxWidth(readVisualViewportRect().width);
}

function useLoginCardMaxWidth(): number {
  const [maxWidth, setMaxWidth] = useState(readLoginCardMaxWidth);
  useLayoutEffect(() => {
    if (typeof window === "undefined") return undefined;
    const sync = (): void => setMaxWidth(readLoginCardMaxWidth());
    sync();
    return addVisualViewportListeners(sync);
  }, []);
  return maxWidth;
}

function LoginNotice({ message }: { readonly message: string }) {
  return (
    <div className="relative z-10 mb-5 rounded-lg border border-warning bg-warning/10 px-4 py-2 text-center text-sm text-warning">
      {message}
    </div>
  );
}

function loginNotice(params: URLSearchParams): string | null {
  const reason = params.get("reason");
  if (reason === "suspended") return "Conta temporariamente bloqueada";
  return null;
}

function LoginWhatsAppFab(): React.JSX.Element | null {
  const support = useSupportWhatsApp();
  if (support.href === null) {
    return null;
  }
  return (
    <div className="pointer-events-none fixed inset-0 z-[9999]">
      <div className="absolute inset-0 mx-auto w-full max-w-[430px]">
        <div className="absolute bottom-[88px] right-6">
          <a
            href={support.href}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Abrir suporte do QuartelBot no WhatsApp"
            data-testid="support-whatsapp-fab"
            className={LOGIN_FAB_LINK_CLASS_NAME}
          >
            <img
              src="/icons/whatsapp-official.svg"
              alt="WhatsApp"
              className="h-7 w-7"
            />
            <span className={LOGIN_FAB_TOOLTIP_CLASS_NAME} role="tooltip">
              Precisa de ajuda?
            </span>
          </a>
        </div>
      </div>
    </div>
  );
}

// ── Public export ──────────────────────────────────────────────────────────────

// `/login` mounts the form first so audit/RID + no-postlogin contracts can
// drive the inputs (C46-AUDIT-LOGIN-RID, C46-AUTH-NO-POSTLOGIN). Already-
// authenticated visitors that don't touch the form within the grace window
// fall through to the AuthenticatedLoginGate skeleton + redirect (C46-LOGIN-
// WHEN-LOGGED-IN-DIRECT, C50-LOGIN-DIRECT-SKELETON). Successful login
// navigation lives in `LoginFormContext` (`onSuccess`) → routes to
// `from ?? "/"` directly, never through `/post-login`.
export default function LoginPage(): React.JSX.Element {
  const plansQuery = useQuery<readonly PublicPlanView[]>({
    queryKey: ["auth", "plans"],
    queryFn: fetchPublicPlans,
    retry: false,
    staleTime: 5 * 60_000,
  });

  const plans = plansQuery.data ?? [];

  return (
    <LoginFormProvider>
      <LoginPageInner plans={plans} />
    </LoginFormProvider>
  );
}

function LoginPageInner({
  plans,
}: {
  plans: readonly PublicPlanView[];
}): React.JSX.Element {
  const { session, isAuthenticated, isLoading } = useSession();
  const flow = useLoginFormContext();
  const location = useLocation();
  const [skipState, setSkipState] = useState<"form" | "redirect">("form");
  const hasInteracted =
    flow.email.length > 0 || flow.password.length > 0 || flow.step !== "email";
  const shouldRedirect =
    !isLoading &&
    isAuthenticated &&
    shouldSkipLoginPage(session) &&
    !hasInteracted;

  useEffect(() => {
    if (skipState === "redirect") return;
    if (!shouldRedirect) return;
    const timer = window.setTimeout(() => {
      setSkipState("redirect");
    }, SKIP_REDIRECT_GRACE_MS);
    return () => window.clearTimeout(timer);
  }, [skipState, shouldRedirect]);

  if (skipState === "redirect") {
    return (
      <AuthenticatedLoginGate
        to={readSkipDestination(location.state, session)}
      />
    );
  }
  // C50-LOGIN-DIRECT-SKELETON (#54:58 + #54:504): when the visitor is
  // already authenticated and the session is still resolving, mount the
  // `app-mobile` skeleton synchronously so Playwright's `toBeVisible`
  // poll catches the transition before the AuthenticatedLoginGate timer
  // swaps in. Holds the existing skip-grace window — users that have
  // already typed into the form still see LoginCard (`hasInteracted`).
  if (shouldRedirect) {
    return (
      <AuthenticatedLoginGate
        to={readSkipDestination(location.state, session)}
      />
    );
  }
  return <LoginCard plans={plans} />;
}

function AuthenticatedLoginGate({ to }: { to: string }): React.JSX.Element {
  const navigate = useNavigate();
  useEffect(() => {
    // Give Playwright's `toBeVisible` poll a frame to catch the skeleton
    // before the route change unmounts us (oracle #54:58 / C50-LOGIN-DIRECT-
    // SKELETON expects the skeleton observable during the transition).
    const timer = window.setTimeout(() => {
      navigate(to, { replace: true });
    }, 150);
    return () => window.clearTimeout(timer);
  }, [navigate, to]);
  return (
    <div
      className="min-h-svh bg-background"
      aria-busy="true"
      data-skeleton="app-mobile"
    />
  );
}

// License states that legitimately keep the visitor on /login (they still
// need to pick or renew a plan from the form). For any other licenseState
// (typically `active`), an already-authenticated visitor is auto-redirected.
const CHECKOUT_LOGIN_STATES = new Set([
  "none",
  "expired",
  "canceled_within",
  "canceled_expired",
]);

function shouldSkipLoginPage(session: SessionView | undefined): boolean {
  if (!session) return false;
  // matrix_user não tem plano e nunca compra — o licenseState "none" não
  // pode prendê-lo no form de planos; o destino é /matrix (09/07/2026).
  if (session.user.role === "matrix_user") return true;
  const licenseState = session.user.licenseState;
  return !licenseState || !CHECKOUT_LOGIN_STATES.has(licenseState);
}

function readSkipDestination(
  state: unknown,
  session: SessionView | undefined,
): string {
  // Operador 09/07/2026: matrix_user autenticado que visita /login vai
  // SEMPRE pro /matrix — nunca pro app, mesmo com state.from apontando lá.
  if (session?.user.role === "matrix_user") return "/matrix";
  if (state === null || typeof state !== "object") return "/";
  const from = (state as { readonly from?: unknown }).from;
  if (typeof from !== "string") return "/";
  if (from === "/login" || from === "/post-login") return "/";
  return from;
}

// Grace window between confirming auth and switching the page over to the
// AuthenticatedLoginGate skeleton. Short enough that
// C46-LOGIN-WHEN-LOGGED-IN-DIRECT's 5s URL poll observes the redirect, long
// enough that Playwright fill+click sequences flip `hasInteracted` first.
const SKIP_REDIRECT_GRACE_MS = 400;
